import { Link } from 'react-router-dom';
import {
  BookOpen,
  FileText,
  FolderKanban,
  Inbox,
  Stethoscope,
  ShieldCheck,
  Search,
  Command,
  Sparkles,
  Mail,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export function HelpPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Help centre</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Task-oriented guides for the everyday jobs — regulatory submissions, PV, compliance.
          For deeper questions contact <a href="mailto:support@praxara.io" className="text-primary underline">support@praxara.io</a>.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Guide
          icon={FolderKanban}
          title="Start a new regulatory submission"
          steps={[
            'Projects to New project to pick "EU MAA" or "US IND".',
            'On the project page, click Upload docs. Drag a folder or ZIP — module structure is inferred from the path (e.g. M3/3.2.S/...).',
            'Each doc is auto-classified (CSR, SmPC, CMC section, etc). Correct the type inline if the classifier gets it wrong.',
            'The eCTD Readiness tab shows green/amber/red per module — gap list updates as you upload.',
            'When a module is ready, click Run workflow to "Gap analysis" or "Cross-reference check" to QC it.',
            'Approve findings with e-signature. Export PDF report for submission package.',
          ]}
        />

        <Guide
          icon={Stethoscope}
          title="Process an adverse event report (ICSR)"
          steps={[
            'Pharmacovigilance to New case intake. Fill patient initials, event verbatim, product, seriousness.',
            'Click "Suggest MedDRA code" — Gemini returns PT/LLT/SOC in ~5 seconds. Review against MedDRA (qualified coder must verify).',
            'Click "Draft narrative" — AI composes the case narrative per ICH E2D/E2B.',
            'Advance through status transitions: TRIAGE to DATA ENTRY to CODING to MEDICAL REVIEW to NARRATIVE to QC to SUBMISSION READY.',
            'QPPV approves with e-signature at each gate.',
            'Export E2B(R3) XML — ready to send to authorities.',
          ]}
        />

        <Guide
          icon={ShieldCheck}
          title="Prepare for a PSMF inspection"
          steps={[
            'Projects to New project to "PSMF inspection readiness".',
            'Upload your PSMF, SOPs, deviation logs, CAPA records into the project.',
            'Compliance scanner to New scan to pick "PSMF review" or "SOP review".',
            'Scanner runs every rule in the embedded GVP Module II + SOP rule set (no external license needed).',
            'For each finding: evidence, citation, recommendation. Close findings as you address them.',
            'Readiness score updates live. Export a PDF of the register before audit week.',
          ]}
        />

        <Guide
          icon={Search}
          title="Search across every document"
          steps={[
            'Press Ctrl/Cmd-K to open the command palette — type keywords to jump anywhere or find a specific document.',
            'For semantic queries (plain-English questions): Workspace to Search.',
            'Ask "adverse events related to the study drug" or "stability data at 40 degrees" — it surfaces the most relevant passages across the tenant.',
            'Results show match percentage + which chunk + direct link to the document.',
          ]}
        />

        <Guide
          icon={Inbox}
          title="Manage your review queue"
          steps={[
            'Workspace to My queue shows every workflow run awaiting your e-signature.',
            'Overdue items highlighted in red — SLA is set by the workflow type (default 5 business days).',
            'Unassigned items at the bottom — click Claim to take one.',
            'Managers can reassign from the Team queue (Administration menu).',
          ]}
        />

        <Guide
          icon={FileText}
          title="Review AI extractions"
          steps={[
            'Side-by-side view: PDF on the left, AI-extracted structured JSON on the right.',
            'Verify each claim against the source. Use in-doc Find (Ctrl-F equivalent) to jump to exact quotes.',
            'Three decisions: Approve (as drafted), Submit corrections (edit JSON + save), Reject (return to start).',
            'Every decision requires password + reason code (21 CFR Part 11 compliant).',
            'Optional free-text notes for context captured in the audit trail.',
          ]}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Keyboard shortcuts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 text-sm md:grid-cols-2">
            <Shortcut keys="Ctrl / Cmd + K" desc="Open command palette" />
            <Shortcut keys="Esc" desc="Close dialogs" />
            <Shortcut keys="? / ?" desc="Navigate command palette results" />
            <Shortcut keys="?" desc="Open selected command palette item" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Compliance references</CardTitle>
          <CardDescription>Every AI output is a decision-support aid, not a regulatory deliverable. A qualified human must review and sign.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p><ShieldCheck className="mr-1 inline h-3.5 w-3.5 text-emerald-600" /> <strong className="text-foreground">21 CFR Part 11</strong> — Section 11.10, Section 11.100, Section 11.300. Electronic records + signatures. Every action is audited with SHA-256-chained integrity.</p>
          <p><ShieldCheck className="mr-1 inline h-3.5 w-3.5 text-emerald-600" /> <strong className="text-foreground">EU AI Act</strong> — Annex III high-risk categorisation. Model cards + risk register under /admin/ai-governance.</p>
          <p><ShieldCheck className="mr-1 inline h-3.5 w-3.5 text-emerald-600" /> <strong className="text-foreground">GDPR</strong> — data subject requests under /admin/gdpr. Full DPA at <Link to="/legal/dpa" className="text-primary">legal/dpa</Link>.</p>
          <p><ShieldCheck className="mr-1 inline h-3.5 w-3.5 text-emerald-600" /> <strong className="text-foreground">ICH E2B(R3)</strong> — full ICSR XML export compatible with EudraVigilance + FAERS gateways.</p>
          <p><ShieldCheck className="mr-1 inline h-3.5 w-3.5 text-emerald-600" /> <strong className="text-foreground">GVP Module II</strong> — PSMF scanner embedded with current EMA rule set.</p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex items-center gap-3 p-4 text-sm">
          <Mail className="h-5 w-5 text-primary shrink-0" />
          <div>
            Stuck on something? Email <a href="mailto:support@praxara.io" className="text-primary underline">support@praxara.io</a> or your designated Customer Success contact. Average response: 2 business hours on Enterprise.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Guide({ icon: Icon, title, steps }: { icon: React.ComponentType<{ className?: string }>; title: string; steps: string[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-primary" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-2 text-sm">
          {steps.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-medium text-primary">{i + 1}</span>
              <span className="text-muted-foreground">{s}</span>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

function Shortcut({ keys, desc }: { keys: string; desc: string }) {
  return (
    <div className="flex items-center justify-between rounded-md border px-3 py-2">
      <span className="text-muted-foreground">{desc}</span>
      <kbd className="rounded border bg-muted px-2 py-0.5 font-mono text-xs">{keys}</kbd>
    </div>
  );
}

// keep unused imports from being stripped
void BookOpen; void Command; void Sparkles;
